import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  CircularProgress,
  Switch,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import {
  Business,
  LocationOn,
  AttachMoney,
  Description,
  Photo,
  Close,
  Save,
} from '@mui/icons-material';
import api from '../services/api';
import AddressAutocompleteField from '../components/AddressAutocompleteField';

export default function EditPropertyModal({ open, onClose, property, vendorId }) {
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10MB
  const MAX_IMAGE_COUNT = 20;

  const parseMoney = (value) => {
    const raw = (value ?? '').toString().trim();
    const normalized = raw.replace(/[$,\s]/g, '');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : NaN;
  };

  const parseHttpUrl = (input) => {
    const raw = (input ?? '').toString().trim();
    if (!raw) return null;
    const candidate = raw.includes('://') ? raw : `https://${raw}`;
    try {
      const u = new URL(candidate);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
      if (u.hostname !== 'localhost' && !u.hostname.includes('.')) return null;
      return u;
    } catch {
      return null;
    }
  };

  const normalizeHttpUrl = (input) => {
    const u = parseHttpUrl(input);
    return u ? u.toString() : '';
  };

  const isProbablyUrl = (v) => typeof v === 'string' && /^https?:\/\//i.test(v);


  // Helper function to normalize status values
  const normalizeStatus = (status) => {
    if (!status) return 'available';
    const statusMap = {
      'Available': 'available',
      'available': 'available',
      'On-Hold': 'on-hold',
      'on-hold': 'on-hold',
      'First Come First Serve': 'first come first serve',
      'first come first serve': 'first come first serve',
      'Vacant on Lockbox': 'vacant on lockbox',
      'vacant on lockbox': 'vacant on lockbox',
      'Under Contract': 'under contract',
      'under contract': 'under contract',
      'Pending': 'pending',
      'pending': 'pending',
      'Sold': 'sold',
      'sold': 'sold'
    };
    return statusMap[status] || 'available';
  };

  const normalizeVisibility = (visibility) => {
    if (!visibility) return 'public';
    const visibilityMap = {
      'Public': 'public',
      'public': 'public',
      'Private': 'private',
      'private': 'private',
      'Draft': 'draft',
      'draft': 'draft'
    };
    return visibilityMap[visibility] || 'public';
  };

  const normalizeLotSizeUnit = (unit) => {
    if (!unit) return 'sqft';
    const u = String(unit).trim().toLowerCase();
    if (u === 'sqft' || u === 'sq ft' || u === 'squarefeet' || u === 'square_feet' || u === 'square feet') return 'sqft';
    if (u === 'acres' || u === 'acre') return 'acres';
    return 'sqft';
  };

  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    squareFootage: '',
    lotSize: '',
    lotSizeUnit: 'sqft',
    askingPrice: '',
    hoaFee: '',
    spaceRent: '',
    closeOfEscrow: '',
    conventionalFinancingAvailable: false,
    status: 'available',
    visibility: 'public',
    description: '',
    features: [],
    uploadedImages: [],
    externalImageUrls: [],
    igGuideUrl: '',
    uploadedDisclosures: [],
    latitude: null,
    longitude: null,
    placeId: null,
    formattedAddress: null,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const disclosuresUploadsInFlightRef = React.useRef(0);
  const [disclosuresUploading, setDisclosuresUploading] = useState(false);
  const [, setCategoriesLoading] = useState(true);
  const [categoriesData, setCategoriesData] = useState({
    propertyTypes: [],
    locations: [],
    features: []
  });

  useEffect(() => {
    if (property && open) {
      setFormData({
        address: property.address || '',
        city: property.city || '',
        state: property.state || '',
        zipCode: property.zipCode || '',
        propertyType: property.propertyType || '',
        bedrooms: property.bedrooms || '',
        bathrooms: property.bathrooms || '',
        squareFootage: property.squareFootage || '',
        lotSize: property.lotSize || '',
        lotSizeUnit: normalizeLotSizeUnit(property.lotSizeUnit),
        askingPrice: property.price || '',
        hoaFee: property.hoaFee || '',
        spaceRent: property.spaceRent || '',
        closeOfEscrow: property.closeOfEscrow || '',
        conventionalFinancingAvailable: property.conventionalFinancingAvailable || false,
        status: normalizeStatus(property.status),
        visibility: normalizeVisibility(property.visibility),
        description: property.description || '',
        features: property.features || [],
        uploadedImages: property.images ? property.images.map(img => ({ preview: img })) : [],
        externalImageUrls: property.externalImageUrls || [],
        igGuideUrl: property.igGuideUrl || '',
        uploadedDisclosures: property.disclosures ? property.disclosures.map(disclosure => ({
          ...disclosure,
          uploadedAt: disclosure.uploadedAt || new Date().toISOString()
        })) : [],
        latitude: property.latitude ?? null,
        longitude: property.longitude ?? null,
        placeId: property.placeId || null,
        formattedAddress: property.formattedAddress || null,
      });
    }
  }, [property, open]);


  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.categories.getAll();
        setCategoriesData(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to hardcoded data if API fails
        setCategoriesData({
          propertyTypes: [
            { id: 'single-family', name: 'Single Family Home' },
            { id: 'multi-family', name: 'Multi-Family Home' },
            { id: 'condo', name: 'Condo' },
            { id: 'townhouse', name: 'Townhouse' },
            { id: 'duplex', name: 'Duplex' },
            { id: 'commercial', name: 'Commercial' },
            { id: 'land', name: 'Land' },
            { id: 'other', name: 'Other' }
          ],
          locations: [],
          features: []
        });
      } finally {
        setCategoriesLoading(false);
      }
    };

    if (open) {
      fetchCategories();
    }
  }, [open]);

  const propertyTypes = Array.isArray(categoriesData.propertyTypes)
    ? categoriesData.propertyTypes.map((type) =>
        typeof type === 'string' ? { id: type, name: type } : type
      )
    : [];

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const tooLarge = files.filter((f) => (f?.size || 0) > MAX_UPLOAD_BYTES);
    if (tooLarge.length > 0) {
      setErrors((prev) => ({
        ...prev,
        submit: `Images must be 10MB or less. Please remove: ${tooLarge.map((f) => f.name).join(', ')}`,
      }));
      try { event.target.value = ''; } catch {}
      return;
    }

    const existingCount = formData.uploadedImages?.length || 0;
    const remaining = Math.max(0, MAX_IMAGE_COUNT - existingCount);
    if (remaining <= 0) {
      setErrors((prev) => ({ ...prev, submit: `You can upload up to ${MAX_IMAGE_COUNT} images.` }));
      try { event.target.value = ''; } catch {}
      return;
    }

    const accepted = files.slice(0, remaining);
    if (accepted.length < files.length) {
      setErrors((prev) => ({
        ...prev,
        submit: `Only the first ${remaining} image(s) were added (max ${MAX_IMAGE_COUNT} total).`,
      }));
    }

    const imageFiles = accepted.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setFormData((prev) => ({
      ...prev,
      uploadedImages: [...(prev.uploadedImages || []), ...imageFiles],
    }));
    try { event.target.value = ''; } catch {}
  };

  const handleDisclosureUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const tooLarge = files.filter((f) => (f?.size || 0) > MAX_UPLOAD_BYTES);
    if (tooLarge.length > 0) {
      setErrors((prev) => ({
        ...prev,
        submit: `Disclosure files must be 10MB or less. Please remove: ${tooLarge.map((f) => f.name).join(', ')}`,
      }));
      // Clear the input so user can re-select.
      try { event.target.value = ''; } catch {}
      return;
    }

    disclosuresUploadsInFlightRef.current += 1;
    setDisclosuresUploading(true);
    try {
      // Upload files to Firebase Storage
      const uploadPath = `properties/${vendorId || property?.vendorId || 'temp'}/disclosures`;
      const uploadResult = await api.storage.uploadFiles(files, uploadPath);

      // Store the uploaded file information
      const disclosureFiles = uploadResult.data.map(upload => ({
        name: upload.name,
        size: upload.size,
        type: upload.type,
        downloadURL: upload.downloadURL,
        path: upload.path,
        uploadedAt: upload.uploadedAt
      }));

      setFormData(prev => ({
        ...prev,
        uploadedDisclosures: [...prev.uploadedDisclosures, ...disclosureFiles]
      }));
    } catch (error) {
      console.error('Error uploading disclosure files:', error);
      // For now, store as local files if upload fails (fallback)
      const disclosureFiles = files.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        uploadError: true
      }));
      setFormData(prev => ({
        ...prev,
        uploadedDisclosures: [...prev.uploadedDisclosures, ...disclosureFiles]
      }));
    } finally {
      disclosuresUploadsInFlightRef.current = Math.max(0, disclosuresUploadsInFlightRef.current - 1);
      setDisclosuresUploading(disclosuresUploadsInFlightRef.current > 0);
    }
  };

  const handleRemoveDisclosure = async (index) => {
    const disclosure = formData.uploadedDisclosures[index];

    // If the file has a path (meaning it was uploaded to storage), delete it from storage
    if (disclosure.path) {
      try {
        await api.storage.deleteFile(disclosure.path);
      } catch (error) {
        console.error('Error deleting disclosure file from storage:', error);
        // Continue with removing from form even if storage deletion fails
      }
    }

    setFormData(prev => ({
      ...prev,
      uploadedDisclosures: prev.uploadedDisclosures.filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'address' || field === 'city' || field === 'state' || field === 'zipCode'
        ? { latitude: null, longitude: null, placeId: null, formattedAddress: null }
        : {})
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP Code is required';
    if (!formData.propertyType) newErrors.propertyType = 'Property type is required';
    if (!formData.askingPrice || !Number.isFinite(parseMoney(formData.askingPrice))) {
      newErrors.askingPrice = 'Valid asking price is required';
    }
    if (!formData.description.trim()) newErrors.description = 'Description is required';

    const externalUrlRaw = formData.externalImageUrls?.[0] || '';
    if (externalUrlRaw) {
      const normalized = normalizeHttpUrl(externalUrlRaw);
      if (!normalized) {
        newErrors.externalImageUrl = 'Please enter a valid URL (https://...)';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      newErrors.submit = `Please fix the following: ${Object.values(newErrors).join(', ')}`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).filter(k => k !== 'submit').length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (disclosuresUploadsInFlightRef.current > 0) {
      setErrors((prev) => ({ ...prev, submit: 'Please wait for disclosure uploads to finish before saving.' }));
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Geocode the address to get coordinates (only if address changed)
      let geocodeResult = null;
      const addressChanged = formData.address !== property.address ||
                            formData.city !== property.city ||
                            formData.state !== property.state ||
                            formData.zipCode !== property.zipCode;

      if (addressChanged && (formData.latitude == null || formData.longitude == null)) {
        console.log('Address changed, geocoding new address...');
        geocodeResult = await api.geocoding.geocodeAddress(
          formData.address,
          formData.city,
          formData.state,
          formData.zipCode
        );
        console.log('Geocode result:', geocodeResult);
      }

      // Process disclosures - remove any file objects and keep only uploaded file info
      const disclosures = formData.uploadedDisclosures
        .filter(disclosure => !disclosure.file && !disclosure.uploadError) // Only include successfully uploaded files
        .map(disclosure => ({
          name: disclosure.name,
          size: disclosure.size,
          type: disclosure.type,
          downloadURL: disclosure.downloadURL,
          path: disclosure.path,
          uploadedAt: disclosure.uploadedAt
        }));

      // Upload any newly selected image files; keep existing URLs.
      const existingImageUrls = (formData.uploadedImages || [])
        .filter((img) => !img?.file)
        .map((img) => (img && img.preview ? img.preview : (typeof img === 'string' ? img : null)))
        .filter((u) => isProbablyUrl(u));

      const newImageFiles = (formData.uploadedImages || [])
        .map((img) => (img && img.file ? img.file : null))
        .filter(Boolean);

      let newUploadedUrls = [];
      if (newImageFiles.length > 0) {
        const uploadPath = `properties/${vendorId || property?.vendorId || 'temp'}/${property.id}/images`;
        const uploadResult = await api.storage.uploadFiles(newImageFiles, uploadPath);
        newUploadedUrls = (uploadResult?.data || [])
          .map((u) => u && u.downloadURL ? u.downloadURL : null)
          .filter(Boolean);
      }

      // Prepare the property data for update
      const externalImageUrlNormalized = normalizeHttpUrl(formData.externalImageUrls?.[0] || '');
      const externalImageUrls = externalImageUrlNormalized ? [externalImageUrlNormalized] : [];

      const updatedProperty = {
        ...property,
        address: formData.address,
        status: formData.status,
        // Keep visibility as a normalized lowercase enum.
        visibility: formData.visibility,
        latitude: formData.latitude ?? property.latitude ?? null,
        longitude: formData.longitude ?? property.longitude ?? null,
        placeId: formData.placeId ?? property.placeId ?? null,
        formattedAddress: formData.formattedAddress ?? property.formattedAddress ?? null,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        propertyType: formData.propertyType,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        squareFootage: formData.squareFootage ? parseInt(formData.squareFootage) : null,
        lotSize: formData.lotSize ? parseFloat(formData.lotSize) : null,
        lotSizeUnit: normalizeLotSizeUnit(formData.lotSizeUnit),
        price: parseMoney(formData.askingPrice),
        hoaFee: formData.hoaFee ? parseFloat(formData.hoaFee) : null,
        spaceRent: formData.spaceRent ? parseFloat(formData.spaceRent) : null,
        closeOfEscrow: formData.closeOfEscrow || null,
        conventionalFinancingAvailable: formData.conventionalFinancingAvailable,
        description: formData.description,
        features: formData.features,
        images: [...existingImageUrls, ...newUploadedUrls],
        externalImageUrls: externalImageUrls,
        igGuideUrl: formData.igGuideUrl,
        disclosures: disclosures,
        updatedAt: new Date().toISOString()
      };

      // Add geocoding coordinates if address changed and geocoding was successful
      if (geocodeResult && geocodeResult.data.latitude !== null && geocodeResult.data.longitude !== null) {
        updatedProperty.latitude = geocodeResult.data.latitude;
        updatedProperty.longitude = geocodeResult.data.longitude;
        if (geocodeResult.data.formattedAddress) {
          updatedProperty.formattedAddress = geocodeResult.data.formattedAddress;
        }
        if (geocodeResult.data.placeId) {
          updatedProperty.placeId = geocodeResult.data.placeId;
        }
      }

      console.log('Updating property with data:', updatedProperty);

      // Update property via mock API
      const response = await api.properties.update(property.id, updatedProperty);

      console.log('Property updated successfully:', response.data);
      onClose();
    } catch (error) {
      console.error('Error updating property:', error);
      setErrors({ submit: 'Failed to update property. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      address: '',
      city: '',
      state: '',
      zipCode: '',
      propertyType: '',
      bedrooms: '',
      bathrooms: '',
      squareFootage: '',
      lotSize: '',
      lotSizeUnit: 'sqft',
      askingPrice: '',
      visibility: 'public',
      hoaFee: '',
      spaceRent: '',
      closeOfEscrow: '',
      conventionalFinancingAvailable: false,
      status: 'available',
      description: '',
      features: [],
      uploadedImages: [],
      externalImageUrls: [],
      igGuideUrl: '',
      uploadedDisclosures: [],
      latitude: null,
      longitude: null,
      placeId: null,
      formattedAddress: null,
    });
    setErrors({});
    onClose();
  };

  if (!property) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
            <Business sx={{ mr: 1 }} />
            Edit Property
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {errors.submit && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Typography>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Property Address */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <LocationOn sx={{ mr: 1 }} />
            Property Address
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AddressAutocompleteField
                label="Search address"
                value={''}
                onChange={() => {}}
                onPlaceSelected={(parsed) => {
                  setFormData((prev) => ({
                    ...prev,
                    address: parsed.address || prev.address,
                    city: parsed.city || prev.city,
                    state: parsed.state || prev.state,
                    zipCode: parsed.zipCode || prev.zipCode,
                    latitude: parsed.latitude ?? null,
                    longitude: parsed.longitude ?? null,
                    placeId: parsed.placeId || null,
                    formattedAddress: parsed.formattedAddress || null,
                  }));
                }}
                helperText="Optional: select a suggestion to update the address"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                error={!!errors.address}
                helperText={errors.address}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                error={!!errors.city}
                helperText={errors.city}
                required
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                error={!!errors.state}
                helperText={errors.state}
                required
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                required
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Property Details */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Business sx={{ mr: 1 }} />
            Property Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.propertyType}>
                <InputLabel>Property Type</InputLabel>
                  <Select
                    value={
                      propertyTypes.find((type) => type.id === formData.propertyType)
                        ? formData.propertyType
                        : ''
                    }
                    label="Property Type"
                    onChange={(e) => handleInputChange('propertyType', e.target.value)}
                  >
                    {propertyTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                {errors.propertyType && (
                  <Typography variant="caption" color="error">
                    {errors.propertyType}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="on-hold">On-Hold</MenuItem>
                  <MenuItem value="first come first serve">First Come First Serve</MenuItem>
                  <MenuItem value="vacant on lockbox">Vacant on Lockbox</MenuItem>
                  <MenuItem value="under contract">Under Contract</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="sold">Sold</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={formData.visibility}
                  label="Visibility"
                  onChange={(e) => handleInputChange(
                    'visibility',
                    normalizeVisibility(e.target.value)
                  )}
                >
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Bedrooms"
                type="number"
                value={formData.bedrooms}
                onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Bathrooms"
                type="number"
                step="0.5"
                value={formData.bathrooms}
                onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Square Footage"
                type="number"
                value={formData.squareFootage}
                onChange={(e) => handleInputChange('squareFootage', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={8} sm={4}>
              <TextField
                fullWidth
                label="Lot Size"
                type="number"
                value={formData.lotSize}
                onChange={(e) => handleInputChange('lotSize', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={formData.lotSizeUnit}
                  label="Unit"
                  onChange={(e) => handleInputChange('lotSizeUnit', e.target.value)}
                >
                  <MenuItem value="sqft">Square Feet</MenuItem>
                  <MenuItem value="acres">Acres</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Pricing */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachMoney sx={{ mr: 1 }} />
            Pricing
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Asking Price"
                type="text"
                inputMode="numeric"
                value={formData.askingPrice}
                onChange={(e) => handleInputChange('askingPrice', e.target.value)}
                error={!!errors.askingPrice}
                helperText={errors.askingPrice}
                InputProps={{ inputProps: { min: 0 } }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HOA Fee (Monthly)"
                type="number"
                value={formData.hoaFee}
                onChange={(e) => handleInputChange('hoaFee', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Space Rent (Sq Ft/Year)"
                type="number"
                value={formData.spaceRent}
                onChange={(e) => handleInputChange('spaceRent', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Close of Escrow Date"
                type="date"
                value={formData.closeOfEscrow}
                onChange={(e) => handleInputChange('closeOfEscrow', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.conventionalFinancingAvailable}
                    onChange={(e) => handleInputChange('conventionalFinancingAvailable', e.target.checked)}
                  />
                }
                label="Conventional Financing Available"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Description */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Description sx={{ mr: 1 }} />
            Description
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Property Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            required
          />

          <Divider sx={{ my: 3 }} />

          {/* Images */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Photo sx={{ mr: 1 }} />
            Images
          </Typography>

          {/* Current Images */}
          {formData.uploadedImages && formData.uploadedImages.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Images:
              </Typography>
              <Grid container spacing={1}>
                {formData.uploadedImages.map((image, index) => (
                  <Grid item key={index}>
                    <Box
                      component="img"
                      src={image.preview || image}
                      alt={`Property ${index + 1}`}
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid #ddd'
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Upload New Images */}
          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="property-images-upload"
              multiple
              type="file"
              onChange={handleImageUpload}
            />
            <label htmlFor="property-images-upload">
              <Button variant="outlined" component="span" startIcon={<Photo />}>
                Upload New Images
              </Button>
            </label>
          </Box>

          {/* External Image URLs */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="External Image URL"
              value={(formData.externalImageUrls && formData.externalImageUrls[0]) || ''}
              onChange={(e) => {
                const url = (e.target.value || '').trim();
                setFormData((prev) => ({
                  ...prev,
                  externalImageUrls: url ? [url] : [],
                }));
                if (errors.externalImageUrl) {
                  setErrors((prev) => ({ ...prev, externalImageUrl: undefined }));
                }
              }}
              onBlur={() => {
                const raw = formData.externalImageUrls?.[0] || '';
                if (!raw) return;
                const normalized = normalizeHttpUrl(raw);
                if (!normalized) {
                  setErrors((prev) => ({ ...prev, externalImageUrl: 'Please enter a valid URL (https://...)' }));
                  return;
                }
                setFormData((prev) => ({ ...prev, externalImageUrls: [normalized] }));
              }}
              error={Boolean(errors.externalImageUrl)}
              helperText={errors.externalImageUrl || ''}
            />
          </Box>

          {/* Property Disclosures Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
            Property Disclosures
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Upload Disclosure Documents
          </Typography>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt"
            onChange={(e) => handleDisclosureUpload(e)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          {formData.uploadedDisclosures && formData.uploadedDisclosures.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Uploaded Disclosures:
              </Typography>
              {formData.uploadedDisclosures.map((disclosure, index) => (
                <Chip
                  key={index}
                  label={`${disclosure.name || 'Document'} (${disclosure.size ? (disclosure.size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown size'})`}
                  onDelete={() => handleRemoveDisclosure(index)}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}

          {/* iGUIDE Link */}
          <TextField
            fullWidth
            label="iGUIDE/Virtual Tour Link"
            value={formData.igGuideUrl}
            onChange={(e) => handleInputChange('igGuideUrl', e.target.value)}
            helperText="Optional: Add a link to 3D tour or virtual walkthrough"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        {errors.submit && (
          <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
            {errors.submit}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || disclosuresUploading}
          startIcon={<Save />}
          sx={{
            backgroundColor: '#008080',
            '&:hover': { backgroundColor: '#005252' }
          }}
        >
          {loading ? 'Updating...' : 'Update Property'}
        </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
